import {Controller} from "stimulus"

// The planning case index filter bar. Every change rebuilds the index URL from
// all of the controls and navigates to it; Planning::CaseSearch reads it back.
export default class extends Controller {
    static targets = ["active", "jurisdiction", "statuses", "caseTemplates", "dueStart", "dueEnd",
        "appliedStart", "appliedEnd"];

    // Parcels rendered by the server as already selected arrive as plain
    // search results. Tag them the way the picker tags a fresh selection, or
    // the next filter change would silently drop them.
    connect() {
        this.openingStatuses = this.selectedStatuses();
        this.selectedParcelsArea().querySelectorAll('.result-location').forEach(parcel => {
            parcel.classList.add('result-parcel');
            parcel.querySelectorAll('.delete-action').forEach(action => action.classList.remove('hidden'));
        });
    }

    disconnect() {
        clearTimeout(this.delayTimer);
    }

    getReport() {
        this.reloadReport({keepStatuses: true});
    }

    // Multi-selects fire on every pick, so wait for the user to finish.
    getDelayedReport() {
        clearTimeout(this.delayTimer);
        this.delayTimer = setTimeout(() => this.getReport(), 1500);
    }

    // Which statuses are selectable follows the Active checkbox, so toggling
    // it lets the server re-offer them rather than keeping a stale choice.
    toggleActive() {
        this.reloadReport({keepStatuses: false});
    }

    removeParcel(e) {
        e.preventDefault();
        // A freshly picked parcel's list item reloads the report on click
        // too; stop here so removing it navigates only once.
        e.stopPropagation();
        e.target.closest('.result-parcel').remove();
        this.getReport();
    }

    reloadReport({keepStatuses}) {
        const query = new URLSearchParams({
            active: this.activeTarget.checked,
            // The jurisdiction select only renders when more than one
            // jurisdiction exists.
            jurisdiction: this.hasJurisdictionTarget ? this.jurisdictionTarget.value : "",
            case_template_ids: this.selectedValues(this.caseTemplatesTarget),
            due_start: this.dueStartTarget.value,
            due_end: this.dueEndTarget.value,
            applied_start: this.appliedStartTarget.value,
            applied_end: this.appliedEndTarget.value,
            parcels: this.selectedParcelIds()
        });
        // Status labels are free text and may hold commas, so each one
        // travels whole as its own statuses[] value rather than comma-joined.
        if (keepStatuses) this.chosenStatuses().forEach(status => query.append('statuses[]', status));
        const sort = this.data.get('sort');
        if (sort) query.set('sort', sort);
        this.navigateTo("/planning/cases?" + query.toString());
    }

    // "All" and specific statuses exclude each other, but SlimSelect keeps
    // both chosen. Whichever the user picked since the page opened wins: a
    // status added to an opening "All" replaces it, and "All" added to
    // opening statuses clears them.
    chosenStatuses() {
        const current = this.selectedStatuses();
        if (!current.includes('all')) return current;
        if (!this.openingStatuses.includes('all')) return ['all'];

        const specific = current.filter(status => status !== 'all');
        return specific.length > 0 ? specific : ['all'];
    }

    selectedStatuses() {
        return Array.from(this.statusesTarget.selectedOptions).map(option => option.value);
    }

    selectedValues(select) {
        return Array.from(select.selectedOptions).map(option => option.value).join(',');
    }

    selectedParcelIds() {
        return Array.from(this.selectedParcelsArea().querySelectorAll('.result-parcel'))
            .map(parcel => parcel.id).join(',');
    }

    selectedParcelsArea() {
        return this.element.querySelector('.selected-parcels');
    }

    // The single seam that leaves the page; jsdom does not implement
    // navigation, so the tests spy on this.
    navigateTo(url) {
        window.location.href = url;
    }
}
